/**
 * S-78: the payment-gateway configuration of a business (settings.merchant_id, terminal_id,
 * payment_gateway) belongs to Xhinola only (`xhinola_admin`); a tenant admin can read it but not change it.
 */

/**
 * The `settings` to send when saving a business, or undefined to leave `settings` out of the request.
 * Anyone but Xhinola never sends it, so a save cannot clear or rewrite the gateway keys; an Xhinola admin
 * gets the stored settings with merchant_id / terminal_id set, or removed when the field is empty.
 */
export function settingsForSave(
  existingSettings: Record<string, any> | null | undefined,
  merchantId: string,
  terminalId: string,
  isXhinolaAdmin: boolean,
  paymentGateway: GatewayChoice | '' = '',
): Record<string, any> | undefined {
  if (!isXhinolaAdmin) return undefined;
  const merged: Record<string, any> = { ...(existingSettings || {}) };
  // No choice = leave whatever is stored (the backend also reads the older spellings «portal» / «carnet»).
  if (paymentGateway) merged.payment_gateway = paymentGateway;
  const merchant = merchantId.trim();
  const terminal = terminalId.trim();
  if (merchant) merged.merchant_id = merchant; else delete merged.merchant_id;
  if (terminal) merged.terminal_id = terminal; else delete merged.terminal_id;
  return merged;
}

/** The gateway a business charges with: `settings.payment_gateway` (#826). */
export type GatewayChoice = 'cardnet' | 'cybersource';

/** What is stored may use the older spellings the backend still accepts; '' = the business names none. */
export function normalizeGateway(value: unknown): GatewayChoice | '' {
  const v = String(value ?? '').trim().toLowerCase();
  if (v === 'cardnet' || v === 'carnet') return 'cardnet';
  if (v === 'cybersource' || v === 'portal') return 'cybersource';
  return '';
}

/** «•••••9090»: an identifier is shown by its tail only (everything when it is too short to hide a part). */
export function maskTail(value: string | null | undefined, keep = 4): string {
  const v = (value || '').trim();
  if (!v) return '';
  return v.length <= keep ? '•'.repeat(v.length) : `•••••${v.slice(-keep)}`;
}

export type GatewayStatus = { label: string; tone: 'ok' | 'warn' };

export function cardnetStatus(merchantId: string, terminalId: string): GatewayStatus {
  const merchant = merchantId.trim();
  const terminal = terminalId.trim();
  if (merchant && terminal) return { label: 'Configured', tone: 'ok' };
  if (!merchant && !terminal) return { label: 'Missing merchant and terminal', tone: 'warn' };
  return { label: terminal ? 'Missing merchant' : 'Missing terminal', tone: 'warn' };
}

/** CyberSource is configured when an active merchant_config row names a secret for the business. */
export function cybersourceStatus(hasSecret: boolean): GatewayStatus {
  return hasSecret ? { label: 'Configured', tone: 'ok' } : { label: 'Missing secret', tone: 'warn' };
}
